import { Box, Pagination, Typography } from "@mui/material"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { type ChangeEvent, forwardRef, useImperativeHandle, useRef } from "react"
import { useShallow } from "zustand/react/shallow"

import { ProductPricingTable } from "./tables"
import type { ProductPricingTableHandle } from "./tables/ProductPricingTable"

import { InformationMissingAlert } from "@/components/common/Alerts/InformationMissingAlert"
import { Button } from "@/components/common/Button"
import type {
  GetPriceChangeItemsInput,
  PriceChangeFilterInput,
  PriceChangeType,
  Product,
  SubmitPriceChangeItemInput,
} from "@/graphql/codegen/graphql"
import type { PricingDialogs } from "@/screens/Pricing/constants"
import { useGetPriceChangeItems } from "@/screens/Pricing/hooks/useGetPriceChangeItems"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"

export interface PriceChangeSubmitContext {
  filter: PriceChangeFilterInput
  priceChangeItems: SubmitPriceChangeItemInput[]
}

export interface PriceChangeTablesHandle {
  getSubmitContext: () => PriceChangeSubmitContext | null
}

interface PriceChangeTablesProps {
  setDialogOpen: (open: PricingDialogs) => void
  openSubmitDialog: () => void
  setSelectedProduct: (product: Product) => void
}

export const PriceChangeTables = forwardRef<PriceChangeTablesHandle, PriceChangeTablesProps>(
  ({ setDialogOpen, setSelectedProduct, openSubmitDialog }, ref) => {
    const uoms = useUnitOfMeasurementsStore(useShallow((s) => s.units))
    const navigate = useNavigate({ from: "/app/pricing/price-change" })
    const tableRefs = useRef<(ProductPricingTableHandle | null)[]>([])

    /**
     * TODO:
     * dates, validation,
     * apply calculations
     */

    const search = useSearch({ from: "/app/pricing/_layout/price-change" }) as {
      changeType?: string
      supplierId?: string
      effectiveDate?: string
      productCategoryId?: string
      productClassId?: string
      page?: number
    }
    const {
      changeType = "",
      supplierId = "",
      effectiveDate = "",
      productCategoryId = "",
      productClassId = "",
      page = 1,
    } = search

    const filter: PriceChangeFilterInput | null = changeType
      ? {
          changeType: changeType as PriceChangeType,
          ...(supplierId ? { supplierId } : {}),
          ...(effectiveDate ? { effectiveDate } : {}),
          ...(productCategoryId ? { productCategoryId } : {}),
          ...(productClassId ? { productClassId } : {}),
        }
      : null

    const input: GetPriceChangeItemsInput | null = filter ? { filter } : null

    const { priceChangeItems, total, pageCount } = useGetPriceChangeItems(input, page)

    useImperativeHandle(ref, () => ({
      getSubmitContext: () => {
        if (!filter) return null
        const items = tableRefs.current.flatMap((tableRef) => tableRef?.getSubmitItems() ?? [])
        return { filter, priceChangeItems: items }
      },
    }))

    const totalPricingVariations = priceChangeItems.reduce(
      (total, item) => total + item.items.reduce((t, i) => t + i.tiers.length, 0),
      0
    )

    const handlePageChange = (_: ChangeEvent<unknown>, value: number) => {
      navigate({
        search: (prev) => ({ ...prev, page: value === 1 ? undefined : value }),
        replace: true,
      })
    }

    if (!priceChangeItems.length) {
      return (
        <InformationMissingAlert title='Apply filters, optionally set Bulk Update Options, then review and adjust individual items.' />
      )
    }

    return (
      <>
        <Box className='flex items-center justify-between'>
          <Typography variant='body1'>
            Showing {priceChangeItems.length} of {total} products, {totalPricingVariations} total pricing variations.
          </Typography>

          <Button variant='primary' onClick={openSubmitDialog}>
            Submit Price Changes
          </Button>
        </Box>

        {priceChangeItems.map((pcItems, index) => (
          <Box key={pcItems.id} className='mb-6'>
            <ProductPricingTable
              ref={(el: ProductPricingTableHandle | null) => {
                tableRefs.current[index] = el
              }}
              priceChangeItems={pcItems}
              uoms={uoms}
              setDialogOpen={setDialogOpen}
              setSelectedProduct={setSelectedProduct}
            />
          </Box>
        ))}

        <Box className='flex items-center justify-center'>
          <Pagination
            count={Math.max(pageCount, 1)}
            showFirstButton
            showLastButton
            page={page}
            onChange={handlePageChange}
            color='primary'
            data-testid='price-change.tables.pagination'
          />
        </Box>
      </>
    )
  }
)

PriceChangeTables.displayName = "PriceChangeTables"
